import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Search } from "lucide-react";

function fmt(d) { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return d; } }

function daysUntil(d) {
  if (!d) return null;
  const t = new Date(d); if (isNaN(t.getTime())) return null;
  const today = new Date(); today.setHours(0,0,0,0); t.setHours(0,0,0,0);
  return Math.round((t - today) / 86400000);
}

function ExpiryCell({ date }) {
  if (!date) return <span className="text-slate-400 text-xs">—</span>;
  const days = daysUntil(date);
  const iso = new Date(date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  const cls = days == null ? "bg-slate-50 text-slate-700 border-slate-200"
    : days < 0 ? "bg-rose-100 text-rose-900 border-rose-300 font-semibold"
    : days <= 14 ? "bg-rose-50 text-rose-800 border-rose-200 font-semibold"
    : days <= 30 ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-emerald-50 text-emerald-800 border-emerald-200";
  const badge = days == null ? "" : days < 0 ? `EXPIRED ${Math.abs(days)}d ago` : days === 0 ? "TODAY" : `${days}d left`;
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`stage-badge ${cls}`}>{iso}</span>
      <span className={`text-[10px] uppercase tracking-wide ${days!=null && (days<0||days<=14)?"text-rose-700":days!=null && days<=30?"text-amber-700":"text-slate-500"}`}>{badge}</span>
    </div>
  );
}

const stageColor = (s = "") => {
  const map = {
    "New File": "bg-slate-50 text-slate-700 border-slate-200",
    "Documents Pending": "bg-amber-50 text-amber-800 border-amber-200",
    "Preparing": "bg-blue-50 text-blue-800 border-blue-200",
    "Ready for Review": "bg-indigo-50 text-indigo-800 border-indigo-200",
    "Under Review": "bg-indigo-50 text-indigo-800 border-indigo-200",
    "Changes Required": "bg-rose-50 text-rose-800 border-rose-200",
    "Approved for Submission": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Submitted": "bg-teal-50 text-teal-800 border-teal-200",
    "Documents Requested": "bg-amber-50 text-amber-800 border-amber-200",
    "Approved": "bg-green-50 text-green-800 border-green-200",
    "Refused": "bg-red-50 text-red-800 border-red-200",
    "Withdrawn": "bg-slate-50 text-slate-700 border-slate-200",
    "Closed": "bg-slate-50 text-slate-500 border-slate-200",
  };
  return map[s] || "bg-slate-50 text-slate-700 border-slate-200";
};

export default function ImmigrationApplications() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ app_types: [], officers: [], statuses: [], priorities: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [officer, setOfficer] = useState("ALL");
  const [priority, setPriority] = useState("ALL");
  const [includeClosed, setIncludeClosed] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status !== "ALL") params.set("status", status);
    if (type !== "ALL") params.set("app_type_id", type);
    if (officer !== "ALL") params.set("assigned_officer_id", officer);
    if (priority !== "ALL") params.set("priority", priority);
    if (includeClosed) params.set("include_closed", "true");
    const [r, m] = await Promise.all([
      api.get(`/immigration/applications?${params.toString()}`),
      api.get("/immigration/meta"),
    ]);
    setRows(r.data); setMeta(m.data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status, type, officer, priority, includeClosed]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Immigration Applications</h1>
          <p className="text-sm text-slate-500 mt-1">{rows.length} shown</p>
        </div>
        <Button data-testid="imm-new-app-btn" onClick={()=>nav("/immigration/new")} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="w-4 h-4 mr-2"/>New Application
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="imm-search" placeholder="Search name, email, phone, UCI, IRCC#…" className="pl-9" value={q} onChange={e=>setQ(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="imm-status-filter"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            {(meta.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="imm-type-filter"><SelectValue placeholder="Type"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All types</SelectItem>
            {meta.app_types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={officer} onValueChange={setOfficer}>
          <SelectTrigger data-testid="imm-officer-filter"><SelectValue placeholder="Officer"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All officers</SelectItem>
            {meta.officers.map(o => <SelectItem key={o.user_id} value={o.user_id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-600">
        <label className="flex items-center gap-2">
          <input type="checkbox" data-testid="imm-include-closed" checked={includeClosed} onChange={e=>setIncludeClosed(e.target.checked)} />
          Include closed / decided files
        </label>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Priority"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All priorities</SelectItem>
            {(meta.priorities || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">ID</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">Officer</th>
              <th className="text-left px-4 py-3">Status Expiry</th>
              <th className="text-left px-4 py-3">Received</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(a => (
              <tr key={a.id} data-testid={`imm-row-${a.application_number}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={()=>nav(`/immigration/applications/${a.id}`)}>
                <td className="px-4 py-3 mono text-xs text-orange-600">{a.application_number}</td>
                <td className="px-4 py-3 font-medium">{a.client_name}<div className="text-xs text-slate-500">{a.client_email}</div></td>
                <td className="px-4 py-3">{a.app_type_name}</td>
                <td className="px-4 py-3"><span className={`stage-badge ${stageColor(a.status)}`}>{a.status}</span></td>
                <td className="px-4 py-3">{a.priority}</td>
                <td className="px-4 py-3 text-slate-600">{a.assigned_officer_name || <span className="text-rose-600">Unassigned</span>}</td>
                <td className="px-4 py-3" data-testid={`imm-expiry-${a.application_number}`}><ExpiryCell date={a.status_expiry_date} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmt(a.created_at)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">No applications match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
